import type { VideoJsPlayer } from 'video.js';

import { useEffect, useState } from 'react';

import { VideoJsNativeEvent } from '../player/videojs-event';

interface TechWithVhs {
  vhs?: VhsHandler;
}

// Partial list of VHS options for stats purposes
interface VhsOptions {
  limitRenditionByPlayerDimensions: boolean;
  llhls: boolean;
  maxPlaylistRetries: number;
  overrideNative: boolean;
  playlistExclusionDuration: number;
  sourceType: string;
  src: string;
  useBandwidthFromLocalStorage: boolean;
  useDevicePixelRatio: boolean;
  useNetworkInformationApi: boolean;
  bufferBasedABR: boolean;
}

interface VhsHandler {
  playlists: {
    media: () => {
      attributes?: {
        BANDWIDTH?: number;
      };
    };
  };
  bandwidth?: number;
  representations?: () => {
    codecs: {
      video?: string;
      audio?: string;
    };
    bandwidth: number;
    width: number;
    height: number;
  }[];
  options: () => VhsOptions;
}

export type PlayerStats = {
  videoBitrate: number;
  measuredBitrate: number;
  representations: {
    bandwidth: number;
    width: number;
    height: number;
    videoCodec?: string;
    audioCodec?: string;
  }[];
  viewport: {
    width: number;
    height: number;
    density: number;
  };
  options?: {
    limitRenditionByPlayerDimensions: boolean;
    llhls: boolean;
    maxPlaylistRetries: number;
    overrideNative: boolean;
    playlistExclusionDuration: number;
    sourceType: string;
    src: string;
    useBandwidthFromLocalStorage: boolean;
    useDevicePixelRatio: boolean;
    useNetworkInformationApi: boolean;
    bufferBasedABR: boolean;
  };
};

export function usePlayerStats(player: VideoJsPlayer) {
  const [stats, setStats] = useState<PlayerStats | null>(null);

  useEffect(() => {
    const updateStats = () => {
      const tech = player.tech(true) as TechWithVhs;

      const stats: PlayerStats = {
        videoBitrate: 0,
        measuredBitrate: 0,
        representations: [],
        viewport: { width: player.currentWidth(), height: player.currentHeight(), density: window.devicePixelRatio },
      };

      if (tech?.vhs) {
        const vhsOptions: VhsOptions = tech.vhs.options();
        stats.options = {
          limitRenditionByPlayerDimensions: vhsOptions.limitRenditionByPlayerDimensions,
          llhls: vhsOptions.llhls,
          maxPlaylistRetries: vhsOptions.maxPlaylistRetries,
          overrideNative: vhsOptions.overrideNative,
          playlistExclusionDuration: vhsOptions.playlistExclusionDuration,
          sourceType: vhsOptions.sourceType,
          src: vhsOptions.src,
          useBandwidthFromLocalStorage: vhsOptions.useBandwidthFromLocalStorage,
          useDevicePixelRatio: vhsOptions.useDevicePixelRatio,
          useNetworkInformationApi: vhsOptions.useNetworkInformationApi,
          bufferBasedABR: vhsOptions.bufferBasedABR,
        };

        const playlist = tech.vhs.playlists.media();

        if (playlist && playlist.attributes && playlist.attributes.BANDWIDTH) {
          // ' kbps'
          stats.videoBitrate = playlist.attributes.BANDWIDTH / 1024;
        }

        if (tech.vhs.bandwidth) {
          // ' kbps'
          stats.measuredBitrate = tech.vhs.bandwidth / 1024;
        }

        if (tech.vhs.representations) {
          stats.representations = tech.vhs.representations().map((rep) => ({
            bandwidth: rep.bandwidth / 1024,
            width: rep.width,
            height: rep.height,
            videoCodec: rep.codecs.video,
            audioCodec: rep.codecs.audio,
          }));
        }
      }

      setStats((prevStats) => ({
        ...prevStats,
        ...stats,
      }));
    };

    window.addEventListener('resize', updateStats);
    player.on(VideoJsNativeEvent.LOADED_METADATA, updateStats);
    player.on(VideoJsNativeEvent.TIME_UPDATE, updateStats);

    return () => {
      window.removeEventListener('resize', updateStats);
      player.off(VideoJsNativeEvent.LOADED_METADATA, updateStats);
      player.off(VideoJsNativeEvent.TIME_UPDATE, updateStats);
    };
  }, [player]);

  return stats;
}
